import client from "services/ngage/api-client";
import {Facility, FacilityData, FacilityDataAPI} from "types/Facility";
import {FeatureCollection} from "geojson";
import {queryString} from "services/string";
import deserializeFacility from "services/ngage/deserializers/facility";
import DataResource from "types/DataResource";
import {snakeKeys} from "../array";
import {transformOpeningHoursForAPI} from "../opening-hours";

export type FacilityFilters = Partial<{
    search: string | null
    mapped: boolean
    published: boolean
    page: number
    per_page: number
    sites?: string
}>

/**
 * Get all facilities (paginated)
 */
const all = async (filters?: FacilityFilters) => {
    const query = queryString(filters, {page: 1, per_page: 100})
    const response = await client.get<DataResource<Facility> | null>(`/facilities?${query}`)

    if(response.data){
        response.data.data = response.data.data.map(deserializeFacility)
    }

    return response.data
}

/**
 * Get all facilities (paginated)
 */
const filtered = async (filters: FacilityFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))
    const response = await client.get<DataResource<Facility>>('/facilities', {params})

    if (response.data) {
        response.data.data = response.data.data.map(deserializeFacility)
    }

    return response.data
}

/**
 * Get all facility positions as a GeoJSON feature collection
 */
const positions = async (excludedIds?: Array<number>) => {
    const facilities = await all({mapped: true})
    const filteredFacilities = facilities?.data.filter(facility => !excludedIds?.includes(facility.id)) || []

    return {
        type: "FeatureCollection",
        features: filteredFacilities.map(facility => ({
            id: facility.id,
            type: "Feature",
            properties: {
                type: 'Facility',
                name: facility.name,
                description: facility.description
            },
            geometry: facility.position
        }))
    } as FeatureCollection
}

/**
 * Get a single facility
 *
 * @param {number} id
 */
const get = async (id: number) => {
    const response = await client.get<Facility>(`/facilities/${id}`)
    return deserializeFacility(response.data)
}

/**
 * Store a new facility
 *
 * @param data
 */
const store = async (data: FacilityData) => {
    const hours = data.hours ? transformOpeningHoursForAPI(data.hours) : null
    const siteData: FacilityDataAPI = {...data, hours};

    const response = await client.post<any>('/facilities', siteData)
    return deserializeFacility(response.data)
}

/**
 * Update an existing facility
 *
 * @param {number} id
 * @param data
 */
const update = async (id: number, data: FacilityData) => {
    const hours = data.hours ? transformOpeningHoursForAPI(data.hours) : null;
    const siteData: FacilityDataAPI = {...data, hours};

    const response = await client.put<any>(`/facilities/${id}`, siteData)
    return deserializeFacility(response.data)
}

/**
 * Permanently delete a facility
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
    const response = await client.delete<null>(`/facilities/${id}`)
    return response.status === 204
}

const facilities = {
    all,
    filtered,
    positions,
    get,
    store,
    update,
    destroy
}

export default facilities;